import { Box, Divider, Grid, MenuItem } from '@mui/material';
import { Stack } from '@mui/system';
import { useAuthApi, useMessage } from 'app/context';
import { useForm } from 'app/hooks';
import { Button, DetailWrapperDiv, TextField, Typography } from 'app/shared';
import { buildErrorDetails, buildErrorMessage, ROUTES } from 'app/utils';

import { ICurrentMedicalCenterUpdate, IMedicalCenterSchema, IUserSchema } from 'generated/axios';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import theme from 'theme';

interface Props {
  loggedUser: IUserSchema | undefined;
}

export const MedicalCenterProfile = ({ loggedUser }: Props) => {
  const { authMedicalCenter } = useAuthApi();
  const navigate = useNavigate();
  const { error, success } = useMessage();

  const [loading, setLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<IMedicalCenterSchema>();
  const [defaultValues, setDefaultValues] = useState<ICurrentMedicalCenterUpdate>();

  const buildRequestFromDetail = (data: IMedicalCenterSchema): ICurrentMedicalCenterUpdate => ({
    language: data.language,
    address: data.address,
    name: data.name,
    phone: data.phone,
    vat_number: data.vat_number,
  });

  useEffect(() => {
    if (detail) setDefaultValues(buildRequestFromDetail(detail));
  }, [detail]);

  //get
  const getLoggedMedicalCenter = useCallback(() => {
    setLoading(true);
    authMedicalCenter
      .webGetCurrentMedicalCenterDetails()
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setDetail(resp.data);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser]);

  useEffect(() => {
    getLoggedMedicalCenter();
  }, [getLoggedMedicalCenter]);

  //update
  const update = useCallback(
    (form: ICurrentMedicalCenterUpdate) => {
      setLoading(true);
      authMedicalCenter
        .webUpdateCurrentMedicalCenter(form)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('medicalCenter.updateSuccess') as string });
          setDetail(resp.data);
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
          setErrors(buildErrorDetails(err));
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedUser],
  );

  const { setErrors, bindSelect, submit } = useForm<ICurrentMedicalCenterUpdate>({
    defaultValues: defaultValues,
    onSubmit: update,
  });

  return (
    <DetailWrapperDiv>
      <Typography detailTitle>{t('common.myProfile')}</Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField disabled label={t('medicalCenter.name')} value={detail?.name} />
        </Grid>
        <Grid size={6}>
          <TextField disabled label={t('medicalCenter.vatNumber')} value={detail?.vat_number} />
        </Grid>
        <Grid size={6}>
          <TextField disabled label={t('medicalCenter.email')} value={detail?.user_email} />
        </Grid>
        <Grid size={6}>
          <TextField disabled label={t('medicalCenter.address')} value={detail?.address} />
        </Grid>
        <Grid size={6}>
          <TextField disabled label={t('medicalCenter.phone')} value={detail?.phone} />
        </Grid>
      </Grid>
      <Divider sx={{ padding: 4 }}>
        <Typography color={theme.palette.grey[600]} variant="caption">
          {t('common.language')}
        </Typography>
      </Divider>
      <Stack direction={'row'} alignItems={'center'}>
        <TextField label={t('common.language')} sx={{ maxWidth: '200px' }} {...bindSelect({ field: 'language' })}>
          <MenuItem key={'it'} value={'it'}>
            IT
          </MenuItem>
          <MenuItem key={'en'} value={'en'}>
            EN
          </MenuItem>
          <MenuItem key={'fr'} value={'fr'}>
            FR
          </MenuItem>
          <MenuItem key={'de'} value={'de'}>
            DE
          </MenuItem>
          <MenuItem key={'es'} value={'es'}>
            ES
          </MenuItem>
          <MenuItem value={undefined}>&nbsp;</MenuItem>
        </TextField>
        <Button onClick={submit} loading={loading}>
          {t('actions.changeLanguage')}
        </Button>
      </Stack>
      <Box padding={4}>
        <Button onClick={() => navigate(ROUTES.changePassword)} loading={loading}>
          {t('actions.changePassword')}
        </Button>
      </Box>
    </DetailWrapperDiv>
  );
};
